import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { InputLabel, Select, FormControl, makeStyles, TextField } from '@material-ui/core';

import { useAdminGetOrganizations } from '../../hooks/get-organizations.hook';
import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import OrganizationsTable from '../../components/Tables/OrganizationsTable.component';
import CreateOrganizationDialog from '../../components/Modals/CreateOrganizationDialog.component';
import { OrganizationType } from '../../types/organization.type';
import { ADMIN_ORGANIZATIONS_APP_PATHS } from '../../constants/organizations-paths.constants';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
});

interface ActionContainerProps {
  typeFilter: string;
  search: string;
  onTypeFilterChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onOrganizationCreated: (organizationId: string) => void;
}

function ActionContainer({
  typeFilter,
  search,
  onTypeFilterChange,
  onSearchChange,
  onOrganizationCreated,
}: ActionContainerProps) {
  const classes = useStyles();

  return (
    <section className={classes.filters}>
      <FormControl variant="outlined" size="small">
        <InputLabel>Type</InputLabel>
        <Select
          native
          defaultValue="all"
          label="Type"
          value={typeFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onTypeFilterChange(e.target.value)}
        >
          <option value="all">Tous</option>
          <option value="concierge">Conciergeries</option>
          <option value="company">Entreprises</option>
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Rechercher"
        type="text"
        variant="outlined"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <div style={{ marginLeft: 'auto' }}>
        <CreateOrganizationDialog onCreated={onOrganizationCreated} />
      </div>
    </section>
  );
}

export default function AdminOrganizationsListView() {
  const classes = useStyles();
  const history = useHistory();

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  const debouncedSearch = useDebounce(search, 400);

  const { isLoading, organizations } = useAdminGetOrganizations({
    page: 1,
    limit: 50,
    sortDirection: 'desc',
    filters: {
      type: typeFilter !== 'all' ? (typeFilter as OrganizationType) : undefined,
      name: debouncedSearch || undefined,
    },
  });

  const handleOrganizationCreated = (organizationId: string) => {
    const path = ADMIN_ORGANIZATIONS_APP_PATHS.ORGANIZATION_DETAILS.replace(':organizationId', organizationId);
    history.push(path);
  };

  return (
    <section className={classes.root}>
      <h2>Organisations</h2>
      <ActionContainer
        typeFilter={typeFilter}
        search={search}
        onTypeFilterChange={setTypeFilter}
        onSearchChange={setSearch}
        onOrganizationCreated={handleOrganizationCreated}
      />
      <OrganizationsTable maxHeight="75svh" organizations={organizations} isLoading={isLoading} />
    </section>
  );
}
